import React, { useContext, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../AuthContext';

const API_BASE = 'https://api.insightos.co.uk';

const navLinkClasses = ({ isActive }) =>
  `rounded-lg px-3 py-2 text-sm font-semibold transition ${
    isActive
      ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
      : 'text-slate-700 hover:bg-sky-100/80 hover:text-slate-900'
  }`;

const parseJsonSafe = async (response) => {
  const text = await response.text();
  if (!text) {
    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    return null;
  }
  let payload;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error('Endpoint returned invalid JSON.');
  }
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.message || `Request failed (${response.status})`);
  }
  return payload;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isWithinRange = (isoValue, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  const d = new Date(isoValue);
  if (Number.isNaN(d.getTime())) return true;
  const dayStart = (s) => { const x = new Date(s); x.setHours(0, 0, 0, 0); return x; };
  const dayEnd = (s) => { const x = new Date(s); x.setHours(23, 59, 59, 999); return x; };
  if (fromDate && d < dayStart(fromDate)) return false;
  if (toDate && d > dayEnd(toDate)) return false;
  return true;
};

const getLeadCredits = (lead) => {
  const companiesHouseHit = Number(lead?.CompaniesHouseHit ?? 0) === 1;
  const filtered = Number(lead?.Filter ?? 0) === 1;
  if (companiesHouseHit) return 3;
  if (filtered) return 2;
  return 0;
};

export default function Billing() {
  const { user } = useContext(AuthContext);
  const permissionLevel = Number(user?.permission ?? user?.Permission ?? 0);
  const accessLevel = String(user?.access ?? user?.Access ?? '').trim().toLowerCase();
  const isFullAccessUser = accessLevel === 'admin' && permissionLevel === 1;
  const isPermissionRestricted = permissionLevel === 2 && !isFullAccessUser;
  const companyNameForMenu = String(user?.companyName ?? user?.CompanyName ?? '').trim();
  const showCompanyNameInMenu = Boolean(user?.insightAccount) && companyNameForMenu.length > 0;
  const hasInsightAccount = Boolean(user?.insightAccount);

  const [leads, setLeads] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [supportOpen, setSupportOpen] = useState(false);
  const [supportText, setSupportText] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState('');
  const [supportSuccess, setSupportSuccess] = useState(false);

  const submitSupport = async () => {
    if (!supportText.trim()) return;
    setSupportLoading(true);
    setSupportError('');
    try {
      const resp = await fetch(`${API_BASE}/leads/avenorsupport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: supportText.trim() }),
      });
      const data = await parseJsonSafe(resp);
      if (!data?.success) throw new Error(data?.message || 'Failed to submit.');
      setSupportSuccess(true);
      setSupportText('');
      setTimeout(() => { setSupportOpen(false); setSupportSuccess(false); }, 2500);
    } catch (err) {
      setSupportError(err?.message || 'Failed to submit support request.');
    } finally {
      setSupportLoading(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [leadsResp, galleryResp] = await Promise.all([
        fetch(`${API_BASE}/leads/leadtable`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }),
        fetch(`${API_BASE}/gallery/add`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }).catch(() =>
          fetch(`${API_BASE}/get/gallery/add`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          })
        ),
      ]);

      const leadsData = await parseJsonSafe(leadsResp);
      const leadRows = Array.isArray(leadsData)
        ? leadsData
        : leadsData?.leads ?? [];

      const galleryData = await parseJsonSafe(galleryResp);
      const galleryRows = Array.isArray(galleryData?.data)
        ? galleryData.data
        : Array.isArray(galleryData)
        ? galleryData
        : [];

      setLeads(leadRows);
      setGalleryItems(galleryRows);
    } catch (err) {
      setError(err?.message || 'Failed to load billing data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const billableLeads = useMemo(() => {
    return leads.filter((lead) => {
      const credits = getLeadCredits(lead);
      if (credits === 0) return false;
      const createdAt = lead?.CreatedAt || lead?.createdAt || '';
      return isWithinRange(createdAt, fromDate, toDate);
    });
  }, [leads, fromDate, toDate]);

  const billableGallery = useMemo(() => {
    return galleryItems.filter((item) => {
      const credits = Number(item?.Credits ?? 0);
      if (credits === 0) return false;
      const createdAt = item?.CreatedAt || item?.createdAt || '';
      return isWithinRange(createdAt, fromDate, toDate);
    });
  }, [galleryItems, fromDate, toDate]);

  const totalLeadCredits = useMemo(
    () => billableLeads.reduce((sum, lead) => sum + getLeadCredits(lead), 0),
    [billableLeads]
  );

  const totalGalleryCredits = useMemo(
    () => billableGallery.reduce((sum, item) => sum + Number(item?.Credits ?? 0), 0),
    [billableGallery]
  );

  const totalCreditsUsed = totalLeadCredits + totalGalleryCredits;

  const glassRootStyle = {
    backgroundImage:
      "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(192,219,255,0.9) 45%, rgba(179,132,255,0.92) 100%), url('/avenorlogobackground.png')",
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat px-3 pb-8 pt-3 sm:px-6"
      style={glassRootStyle}
    >
      {/* ── Nav ── */}
      <div className="sticky top-0 z-40 rounded-xl border border-white/70 bg-white/70 shadow-[0_12px_35px_rgba(79,70,229,0.16)] backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-3 py-3 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img src="/avenor.png" alt="InsightOS Avenor Logo" className="h-8 w-8" />
            <h1 className="text-base font-bold text-slate-900">InsightOS Billing</h1>
            {showCompanyNameInMenu && (
              <span className="rounded-md border border-indigo-200/80 bg-indigo-100/80 px-2 py-0.5 text-xs font-semibold text-indigo-900">
                {companyNameForMenu}
              </span>
            )}
            <nav className="hidden items-center gap-2 md:flex">
              <NavLink to="/leads" end className={navLinkClasses}>Leads</NavLink>
              
            
            </nav>
          </div>
          <div className="flex w-full items-center justify-start gap-2 md:w-auto md:justify-end">
            <button
              type="button"
              onClick={fetchAll}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-900 hover:bg-indigo-100 disabled:opacity-60"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setSupportOpen((v) => !v)}
              className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-900 hover:bg-sky-100"
            >
              Support
            </button>
            <NavLink
              to="/logout"
              className="rounded-md bg-red-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-600"
            >
              Logout
            </NavLink>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="border-t border-white/70 px-3 py-2 md:hidden">
          <nav className="flex items-center gap-2">
            <NavLink to="/leads" end className={navLinkClasses}>Leads</NavLink>
            {!isPermissionRestricted && <span className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-400">Ad Studio</span>}
            {!isPermissionRestricted && <span className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-400">Gallery</span>}
            <NavLink to="/billing" className={navLinkClasses}>Billing</NavLink>
          </nav>
        </div>
      </div>

      <div className="mx-auto mt-6 w-full max-w-7xl space-y-6">

        {/* ── Support panel ── */}
        {supportOpen && (
          <div className="overflow-hidden rounded-2xl border border-sky-200/80 bg-white/85 shadow-[0_10px_35px_rgba(14,165,233,0.18)] backdrop-blur">
            <div className="border-b border-sky-100/80 bg-sky-50/60 px-5 py-3 sm:px-6">
              <h2 className="text-sm font-bold text-sky-900">Contact Support</h2>
              <p className="mt-0.5 text-xs text-sky-700">Describe your issue and we'll get back to you.</p>
            </div>
            <div className="px-5 py-4 sm:px-6">
              <textarea
                value={supportText}
                onChange={(e) => setSupportText(e.target.value)}
                placeholder="Describe your billing query or issue..."
                rows={5}
                className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-300/50"
              />
              {supportError && (
                <p className="mt-2 text-xs font-semibold text-red-600">{supportError}</p>
              )}
              {supportSuccess && (
                <p className="mt-2 text-xs font-semibold text-emerald-600">Request submitted! We'll be in touch shortly.</p>
              )}
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setSupportOpen(false); setSupportText(''); setSupportError(''); setSupportSuccess(false); }}
                  className="rounded-md border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitSupport}
                  disabled={supportLoading || !supportText.trim()}
                  className="rounded-md bg-gradient-to-r from-sky-600 to-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow hover:from-sky-500 hover:to-blue-500 disabled:opacity-60"
                >
                  {supportLoading ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Header banner ── */}
        <section className="overflow-hidden rounded-2xl border border-white/80 bg-white/80 p-5 shadow-[0_24px_65px_rgba(79,70,229,0.2)] sm:p-7">
          <p className="inline-flex items-center rounded-full border border-purple-300/70 bg-purple-100/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-purple-800">
            {hasInsightAccount ? 'All Accounts · ' : ''}Billing Overview
          </p>
          <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-900 sm:text-4xl">Credits Usage</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-700 sm:text-base">
            Track credit consumption across Leads enrichment and Ad Studio generation.
            {hasInsightAccount && ' Showing credit usage for all linked accounts.'}
          </p>
        </section>
        <section className="overflow-hidden rounded-2xl border border-white/80 bg-white/80 p-5 shadow-[0_12px_40px_rgba(79,70,229,0.14)] backdrop-blur sm:p-7">
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/35 bg-white/80 p-8 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur-xl">
            <div className="flex flex-col items-center text-center">
              <img
                src="/stripe-logo.jpg"
                alt="Stripe"
                className="mb-5 h-10 w-auto"
              />
              <h2 className="text-2xl font-semibold text-slate-900">Manage Billing In Stripe</h2>
              <p className="mt-2 max-w-xl text-sm text-slate-600">
                Open your Stripe billing portal to update payment details and manage your subscription.
              </p>

              <a
                href="https://billing.stripe.com/p/login/14A28r6u98Ig2wAgdf4sE00"
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center rounded-xl border border-white/20 bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(37,99,235,0.25)] transition hover:from-indigo-500 hover:to-blue-500"
              >
                Open Stripe Billing
              </a>

              <p className="mt-4 break-all rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs text-indigo-700">
                https://billing.stripe.com/p/login/14A28r6u98Ig2wAgdf4sE00
              </p>
            </div>
          </div>
        </section>

        {/* ── Date filters + stats board ── */}
        <section className="overflow-hidden rounded-2xl border border-white/80 bg-white/80 shadow-[0_12px_40px_rgba(79,70,229,0.14)] backdrop-blur">
          <div className="border-b border-white/60 bg-white/50 px-5 py-4 sm:px-6">
            <h3 className="text-sm font-bold text-slate-800">Date Range Filter</h3>
            <p className="mt-0.5 text-xs text-slate-500">Filter credit usage by creation date (applies to both tables).</p>
          </div>
          <div className="flex flex-wrap items-end gap-4 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-300/50"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-300/50"
              />
            </div>
            {(fromDate || toDate) && (
              <button
                type="button"
                onClick={() => { setFromDate(''); setToDate(''); }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Clear
              </button>
            )}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 gap-3 px-5 pb-5 sm:grid-cols-4 sm:px-6">
            <div className="rounded-xl border border-purple-200/80 bg-gradient-to-br from-purple-50 to-indigo-50 p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-purple-600">Total Credits Used</p>
              <p className="mt-1.5 text-3xl font-black text-purple-900">
                {loading ? <span className="text-base font-semibold text-slate-400">Loading…</span> : totalCreditsUsed}
              </p>
            </div>
            <div className="rounded-xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50 to-blue-50 p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">Lead Credits</p>
              <p className="mt-1.5 text-3xl font-black text-indigo-900">
                {loading ? <span className="text-base font-semibold text-slate-400">Loading…</span> : totalLeadCredits}
              </p>
              <p className="mt-1 text-[11px] text-indigo-500">{billableLeads.length} billable leads</p>
            </div>
            <div className="rounded-xl border border-sky-200/80 bg-gradient-to-br from-sky-50 to-cyan-50 p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-600">Gallery Credits</p>
              <p className="mt-1.5 text-3xl font-black text-sky-900">
                {loading ? <span className="text-base font-semibold text-slate-400">Loading…</span> : totalGalleryCredits}
              </p>
              <p className="mt-1 text-[11px] text-sky-500">{billableGallery.length} adverts</p>
            </div>
            <div className="rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Date Range</p>
              <p className="mt-1.5 text-sm font-bold text-emerald-900">
                {fromDate || toDate
                  ? `${fromDate ? formatDate(fromDate) : 'Any'} → ${toDate ? formatDate(toDate) : 'Any'}`
                  : 'All time'}
              </p>
            </div>
          </div>
        </section>

        {error && (
          <div className="rounded-xl border border-red-300/60 bg-red-50/80 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ── Leads credits table ── */}
        <section className="overflow-hidden rounded-2xl border border-white/80 bg-white/80 shadow-[0_12px_40px_rgba(79,70,229,0.14)] backdrop-blur">
          <div className="border-b border-white/60 bg-white/50 px-5 py-4 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">Lead Credits</h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  Enriched leads (2 credits) and Companies House hits (3 credits).
                </p>
              </div>
              <span className="rounded-full border border-indigo-200/80 bg-indigo-100/80 px-3 py-1 text-xs font-bold text-indigo-900">
                {billableLeads.length} rows · {totalLeadCredits} credits
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="space-y-2 p-5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={`ls-${i}`} className="h-8 animate-pulse rounded-lg bg-indigo-50" />
                ))}
              </div>
            ) : billableLeads.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                No billable leads{fromDate || toDate ? ' in the selected date range' : ''}.
              </div>
            ) : (
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Contact Name</th>
                    <th className="px-4 py-3">Credits Used</th>
                    <th className="px-4 py-3">Created At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100/80">
                  {billableLeads.map((lead, idx) => {
                    const credits = getLeadCredits(lead);
                    const isChHit = Number(lead?.CompaniesHouseHit ?? 0) === 1;
                    return (
                      <tr
                        key={lead?.id ?? lead?.LeadID ?? `lead-${idx}`}
                        className="transition hover:bg-indigo-50/40"
                      >
                        <td className="px-4 py-2.5 font-medium text-slate-800">
                          {String(lead?.Company ?? lead?.CompanyName ?? '—')}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">
                          {String(lead?.Location ?? '—')}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">
                          {String(lead?.ContactName ?? '—')}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                              isChHit
                                ? 'border border-amber-300/70 bg-amber-100/80 text-amber-800'
                                : 'border border-indigo-300/70 bg-indigo-100/80 text-indigo-800'
                            }`}
                          >
                            {credits} {isChHit ? '· CH Hit' : '· Enriched'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500">
                          {formatDate(lead?.CreatedAt ?? lead?.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Gallery credits removed per request */}

      </div>
    </div>
  );
}
